/**
 * Social Page
 * 
 * Workspace for partners who share Try-Ons on WhatsApp & Instagram.
 * Reuses components and logic from Customer Engagement page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Heart, ShoppingCart, Link2, Plus, Copy, ExternalLink,
  X, Sparkles, ToggleLeft, ToggleRight, Activity, CreditCard,
  Loader2, Briefcase, Coffee, Moon, Plane, PartyPopper,
  Search, QrCode, Download, Package
} from 'lucide-react';
import { analyticsAPI, inventoryAPI } from '../../api';
import toast from 'react-hot-toast';
import { QRCodeModal } from '../../components/QRCode';
import { KPICard } from '../../components/customer-engagement';
import InventorySelectionModal from '../../components/InventorySelectionModal';

// Moment configurations
const MOMENT_CONFIG = {
  creative_workday: { label: 'Workday', icon: Briefcase, color: 'blue' },
  unwind: { label: 'Unwind', icon: Coffee, color: 'teal' },
  after_hours: { label: 'Evening', icon: Moon, color: 'purple' },
  escape: { label: 'Vacation', icon: Plane, color: 'orange' },
  gentle_celebration: { label: 'Celebration', icon: PartyPopper, color: 'rose' }
};

const ALL_MOMENTS = ['creative_workday', 'unwind', 'after_hours', 'escape', 'gentle_celebration'];

// ==================== Active Link Table Row ====================
const ActiveLinkTableRow = ({ link, onToggle, onCopy, onShowQR, isToggling, index }) => {
  const isActive = link.status === 'active';
  const shareUrl = `https://nanokart.ai/t/${link.token}`;
  
  const getRemainingDays = () => {
    if (!link.expires_at) return null;
    const now = new Date();
    const expiry = new Date(link.expires_at);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  const remainingDays = getRemainingDays();
  const usedCount = link.total_uses || link.used_count || 0;
  const maxUses = link.max_uses;
  const isExpired = remainingDays !== null && remainingDays <= 0;
  const isLimitReached = maxUses && usedCount >= maxUses;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = () => {
    if (!isActive) {
      return <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">Disabled</span>;
    }
    if (isExpired) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">Expired</span>;
    }
    if (isLimitReached) {
      return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Limit Reached</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">Active</span>;
  };
  
  const getUsageDisplay = () => {
    if (maxUses) {
      const percentage = (usedCount / maxUses) * 100;
      const colorClass = percentage >= 90 ? 'text-red-600' : percentage >= 70 ? 'text-amber-600' : 'text-emerald-600';
      return (
        <div className="flex items-center justify-center gap-1">
          <span className={`font-semibold ${colorClass}`}>{usedCount}</span>
          <span className="text-slate-400">/</span>
          <span className="text-slate-500">{maxUses}</span>
        </div>
      );
    }
    return <span className="font-semibold text-slate-700">{usedCount}</span>;
  };

  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`hover:bg-slate-50 transition-colors ${!isActive ? 'opacity-60' : ''}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {link.product_image_url ? (
            <img 
              src={link.product_image_url} 
              alt={link.product_name}
              className="w-10 h-12 object-cover rounded-lg border border-slate-200"
            />
          ) : (
            <div className="w-10 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
              <Link2 className="w-4 h-4 text-slate-400" />
            </div>
          )}
          <div className="min-w-0">
            <p className="font-medium text-slate-900 truncate max-w-[200px]">
              {link.product_name || link.sku_id || 'Unknown Product'}
            </p>
            <p className="text-xs text-slate-500">{link.sku_id}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-center">{getStatusBadge()}</td>
      <td className="px-4 py-3 text-center">{getUsageDisplay()}</td>
      <td className="px-4 py-3 text-center text-sm text-slate-600">
        {remainingDays !== null ? (
          isExpired ? (
            <span className="text-red-600">Expired</span>
          ) : (
            <span>{remainingDays}d left</span>
          )
        ) : (
          <span className="text-slate-400">No limit</span>
        )}
      </td>
      <td className="px-4 py-3 text-center text-sm text-slate-600">{formatDate(link.created_at)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={() => onCopy(shareUrl)}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            title="Copy link"
          >
            <Copy className="w-4 h-4 text-slate-500" />
          </button>
          <button
            onClick={() => onShowQR(link)}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            title="Show QR"
          >
            <QrCode className="w-4 h-4 text-slate-500" />
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            title="Open link"
          >
            <ExternalLink className="w-4 h-4 text-slate-500" />
          </a>
          <button
            onClick={() => onToggle(link)}
            disabled={isToggling}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            title={isActive ? 'Disable link' : 'Enable link'}
          >
            {isToggling ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : isActive ? (
              <ToggleRight className="w-4 h-4 text-green-600" />
            ) : (
              <ToggleLeft className="w-4 h-4 text-slate-400" />
            )}
          </button>
        </div>
      </td>
    </motion.tr>
  );
};

// ==================== Usage Table Row ====================
const UsageTableRow = ({ session, index }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02 }}
      className="hover:bg-slate-50 transition-colors"
    >
      <td className="px-4 py-3 text-sm text-slate-600">{formatDate(session.created_at)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {session.product_image_url ? (
            <img 
              src={session.product_image_url} 
              alt=""
              className="w-8 h-10 object-cover rounded border border-slate-200"
            />
          ) : null}
          <span className="text-sm text-slate-900">{session.product_name || session.sku_id || '-'}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">{session.customer_phone || '-'}</td>
      <td className="px-4 py-3 text-center">
        {session.liked ? (
          <Heart className="w-4 h-4 text-red-500 fill-red-500 mx-auto" />
        ) : (
          <Heart className="w-4 h-4 text-slate-300 mx-auto" />
        )}
      </td>
      <td className="px-4 py-3 text-center">
        {session.buy_clicked ? (
          <ShoppingCart className="w-4 h-4 text-green-600 mx-auto" />
        ) : (
          <ShoppingCart className="w-4 h-4 text-slate-300 mx-auto" />
        )}
      </td>
      <td className="px-4 py-3 text-center text-sm text-slate-600">
        {session.credits_consumed || 1}
      </td>
    </motion.tr>
  );
};

// ==================== How It Works Modal (Mobile) ====================
const HowItWorksModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              How to Create a Link
            </h3>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          <div className="space-y-3">
            {[
              { step: '1', text: 'Add a dress item with image in Inventory' },
              { step: '2', text: 'Click "Create Public TryOn Link"' },
              { step: '3', text: 'Select item, set limits, add moments' },
              { step: '4', text: 'Click "Create & Copy" to get your link' },
              { step: '5', text: 'Share link on WhatsApp, Instagram, or DMs' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {item.step}
                </span>
                <p className="text-sm text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ==================== Main Social Page ====================
const Social = () => {
  // Data state
  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  
  // UI state
  const [activeTab, setActiveTab] = useState('links'); // 'links' or 'usage'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedLinkForQR, setSelectedLinkForQR] = useState(null);
  
  // Links tab state
  const [linksFilter, setLinksFilter] = useState('all');
  const [linksSearchQuery, setLinksSearchQuery] = useState('');
  const [togglingLinkId, setTogglingLinkId] = useState(null);
  
  // Usage tab state
  const [usagePage, setUsagePage] = useState(1);
  const USAGE_PER_PAGE = 15;
  const [usageSearchQuery, setUsageSearchQuery] = useState('');
  const [usageDateFrom, setUsageDateFrom] = useState('');
  const [usageDateTo, setUsageDateTo] = useState('');
  
  // Create modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [maxUses, setMaxUses] = useState('');
  const [expiryDays, setExpiryDays] = useState('');
  const [selectedMoments, setSelectedMoments] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch data from the same APIs as Customer Engagement page
      const [summaryRes, sessionsRes, linksRes, productsRes] = await Promise.allSettled([
        analyticsAPI.publicTryonSummary(),
        analyticsAPI.publicTryonSessions({ limit: 100 }),
        inventoryAPI.listAllShareLinksWithStats({}),
        inventoryAPI.listProducts({ status: 'active', limit: 100 })
      ]);
      
      // Set summary from API response
      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value.data);
      }
      
      // Set sessions from API response
      if (sessionsRes.status === 'fulfilled') {
        setSessions(sessionsRes.value.data?.sessions || []);
      }
      
      // Set links from API response
      if (linksRes.status === 'fulfilled') {
        setLinks(linksRes.value.data?.share_links || []);
      }
      
      // Set products
      if (productsRes.status === 'fulfilled') {
        setProducts(productsRes.value.data?.products || []);
      }
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort links
  const filteredLinks = links
    .filter(link => {
      if (linksFilter === 'active') return link.status === 'active';
      if (linksFilter === 'disabled') return link.status !== 'active';
      if (linksFilter === 'expired') {
        if (!link.expires_at) return false;
        return new Date(link.expires_at) < new Date();
      }
      return true;
    })
    .filter(link => {
      if (!linksSearchQuery) return true;
      const query = linksSearchQuery.toLowerCase();
      return (
        (link.product_name || '').toLowerCase().includes(query) ||
        (link.sku_id || '').toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      // Sort by created_at descending (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    });

  // Filter usage sessions
  const filteredSessions = sessions
    .filter(s => {
      if (!usageSearchQuery) return true;
      const query = usageSearchQuery.toLowerCase();
      return (
        (s.product_name || '').toLowerCase().includes(query) ||
        (s.sku_id || '').toLowerCase().includes(query) ||
        (s.customer_phone || '').includes(query)
      );
    })
    .filter(s => {
      if (usageDateFrom && new Date(s.created_at) < new Date(usageDateFrom)) return false;
      if (usageDateTo && new Date(s.created_at) > new Date(usageDateTo + 'T23:59:59')) return false;
      return true;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const paginatedSessions = filteredSessions.slice(
    (usagePage - 1) * USAGE_PER_PAGE,
    usagePage * USAGE_PER_PAGE
  );
  const totalUsagePages = Math.ceil(filteredSessions.length / USAGE_PER_PAGE);

  // Handlers
  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied!');
  };

  const handleShowQR = (link) => {
    setSelectedLinkForQR(link);
    setShowQRModal(true);
  };

  const handleToggleLink = async (link) => {
    setTogglingLinkId(link.token);
    try {
      await inventoryAPI.toggleShareLinkStatus(link.token);
      toast.success(link.status === 'active' ? 'Link disabled' : 'Link enabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to update link');
    } finally {
      setTogglingLinkId(null);
    }
  };

  const handleCreateLink = async () => {
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }
    
    setIsCreating(true);
    try {
      const payload = {
        max_uses: maxUses ? parseInt(maxUses) : null,
        expires_in_days: expiryDays ? parseInt(expiryDays) : null,
        enable_moments: selectedMoments.length > 0,
        moments: selectedMoments.length > 0 ? selectedMoments : null
      };
      
      const response = await inventoryAPI.createShareLinkDirect(selectedProduct.product_id, payload);
      const shareUrl = `https://nanokart.ai/t/${response.data.token}`;
      
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link created and copied!');
      
      setShowCreateModal(false);
      setSelectedProduct(null);
      setMaxUses('');
      setExpiryDays('');
      setSelectedMoments([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleExportCSV = (type) => {
    let data, filename, headers;
    
    if (type === 'links') {
      headers = ['Product', 'SKU', 'Status', 'Uses', 'Max Uses', 'Created'];
      data = filteredLinks.map(l => [
        l.product_name || '',
        l.sku_id || '',
        l.status,
        l.total_uses || 0,
        l.max_uses || 'Unlimited',
        l.created_at
      ]);
      filename = 'tryon-links.csv';
    } else {
      headers = ['Date', 'Product', 'Customer', 'Liked', 'Buy Click', 'Credits'];
      data = filteredSessions.map(s => [
        s.created_at,
        s.product_name || s.sku_id || '',
        s.customer_phone || '',
        s.liked ? 'Yes' : 'No',
        s.buy_clicked ? 'Yes' : 'No',
        s.credits_consumed || 1
      ]);
      filename = 'individual-usage.csv';
    }
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    toast.success('Exported successfully!');
  };

  const activeLinkCount = links.filter(l => l.status === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Section A: Hero */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* Left Side - Main CTA */}
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              Share Virtual Try-Ons on WhatsApp & Instagram
            </h1>
            <p className="text-slate-600 text-sm mb-6 max-w-lg">
              Create a link for a single dress. Customers can try it on before buying.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-semibold shadow-sm transition-colors"
                data-testid="create-link-btn"
              >
                <Plus className="w-5 h-5" />
                Create Public TryOn Link
              </button>
              {/* Mobile: How it works link */}
              <button
                onClick={() => setShowHowItWorks(true)}
                className="lg:hidden text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                How it works
              </button>
            </div>
          </div>

          {/* Right Side - Quick Steps (Desktop only) */}
          <div className="hidden lg:block lg:w-[360px] bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              How to Create a Link
            </h3>
            <div className="space-y-2.5">
              {[
                { step: '1', text: 'Add a dress item with image in Inventory' },
                { step: '2', text: 'Click "Create Public TryOn Link"' },
                { step: '3', text: 'Select item, set limits, add moments' },
                { step: '4', text: 'Click "Create & Copy" to get your link' },
                { step: '5', text: 'Share on WhatsApp, Instagram, or DMs' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {item.step}
                  </span>
                  <p className="text-xs text-slate-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Try-Ons"
          value={summary?.total_sessions || 0}
          icon={Users}
          color="bg-blue-500"
          subtitle={`${summary?.unique_customers || 0} unique customers`}
        />
        <KPICard
          title="Buy Clicks"
          value={summary?.total_buy_clicks || 0}
          icon={ShoppingCart}
          color="bg-green-600"
          subtitle={`${summary?.buy_click_rate || 0}% conversion`}
        />
        <KPICard
          title="Total Likes"
          value={summary?.total_likes || 0}
          icon={Heart}
          color="bg-blue-500"
          subtitle={`${summary?.like_rate || 0}% like rate`}
        />
        <KPICard
          title="Credits Used"
          value={summary?.total_credits_consumed || 0}
          icon={CreditCard}
          color="bg-amber-500"
          subtitle={`Across ${activeLinkCount} active links`}
        />
      </div>

      {/* Section B: Operations */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Operations Header */}
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">Operations</h2>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-slate-200 px-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('links')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'links'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-links"
            >
              <Link2 className="w-4 h-4" />
              TryOn Links
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'usage'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-usage"
            >
              <Activity className="w-4 h-4" />
              Individual Usage
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* TryOn Links Tab */}
          {activeTab === 'links' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {['all', 'active', 'disabled', 'expired'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => setLinksFilter(filter)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors capitalize ${
                        linksFilter === filter
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search links..."
                      value={linksSearchQuery}
                      onChange={(e) => setLinksSearchQuery(e.target.value)}
                      className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-48 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={() => handleExportCSV('links')}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>

              {/* Links Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-y border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Uses</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Expires</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Created</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredLinks.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center">
                          <Link2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500">No links found</p>
                          <button
                            onClick={() => setShowCreateModal(true)}
                            className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Create your first link
                          </button>
                        </td>
                      </tr>
                    ) : (
                      filteredLinks.map((link, index) => (
                        <ActiveLinkTableRow
                          key={link.token}
                          link={link}
                          index={index}
                          onCopy={handleCopyLink}
                          onShowQR={handleShowQR}
                          onToggle={handleToggleLink}
                          isToggling={togglingLinkId === link.token}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Individual Usage Tab */}
          {activeTab === 'usage' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={usageDateFrom}
                    onChange={(e) => setUsageDateFrom(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-lg"
                  />
                  <span className="text-slate-400">to</span>
                  <input
                    type="date"
                    value={usageDateTo}
                    onChange={(e) => setUsageDateTo(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-lg"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={usageSearchQuery}
                      onChange={(e) => setUsageSearchQuery(e.target.value)}
                      className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-48 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={() => handleExportCSV('usage')}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>

              {/* Usage Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-y border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Liked</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Buy Click</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Credits</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedSessions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center">
                          <Activity className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500">No usage data found</p>
                        </td>
                      </tr>
                    ) : (
                      paginatedSessions.map((session, index) => (
                        <UsageTableRow key={session.session_id || index} session={session} index={index} />
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalUsagePages > 1 && (
                <div className="flex justify-center gap-2 pt-4">
                  <button
                    onClick={() => setUsagePage(p => Math.max(1, p - 1))}
                    disabled={usagePage === 1}
                    className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm text-slate-600">
                    Page {usagePage} of {totalUsagePages}
                  </span>
                  <button
                    onClick={() => setUsagePage(p => Math.min(totalUsagePages, p + 1))}
                    disabled={usagePage === totalUsagePages}
                    className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Link Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">Create Try-On Link</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="p-6 space-y-5">
                {/* Product Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Product</label>
                  <button
                    onClick={() => setShowInventoryModal(true)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-left hover:border-blue-300 hover:bg-slate-50 transition-colors flex items-center justify-between"
                    data-testid="select-product-btn"
                  >
                    {selectedProduct ? (
                      <div className="flex items-center gap-3">
                        {(selectedProduct.tryon_input_image_url || selectedProduct.image_url) && (
                          <img 
                            src={selectedProduct.tryon_input_image_url || selectedProduct.image_url} 
                            alt={selectedProduct.product_name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <p className="text-sm font-medium text-slate-900">{selectedProduct.product_name || selectedProduct.sku_id}</p>
                          <p className="text-xs text-slate-500">{selectedProduct.sku_id}</p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-400">Choose a product...</span>
                    )}
                    <Package className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                {/* Max Uses */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Max Uses (optional)</label>
                  <input
                    type="number"
                    min="1"
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    placeholder="Unlimited"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Expiry */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Expires In (days, optional)</label>
                  <input
                    type="number"
                    min="1"
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value)}
                    placeholder="Never expires"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Moments */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Moments (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {ALL_MOMENTS.map(momentId => {
                      const config = MOMENT_CONFIG[momentId];
                      const Icon = config.icon;
                      const isSelected = selectedMoments.includes(momentId);
                      return (
                        <button
                          key={momentId}
                          onClick={() => {
                            setSelectedMoments(prev => 
                              isSelected 
                                ? prev.filter(m => m !== momentId)
                                : [...prev, momentId]
                            );
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                            isSelected
                              ? 'bg-blue-100 text-blue-700 border border-blue-200'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateLink}
                  disabled={!selectedProduct || isCreating}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create & Copy
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* How It Works Modal (Mobile) */}
      <HowItWorksModal isOpen={showHowItWorks} onClose={() => setShowHowItWorks(false)} />

      {/* QR Code Modal */}
      {showQRModal && selectedLinkForQR && (
        <QRCodeModal
          isOpen={showQRModal}
          onClose={() => {
            setShowQRModal(false);
            setSelectedLinkForQR(null);
          }}
          url={`https://nanokart.ai/t/${selectedLinkForQR.token}`}
          title={selectedLinkForQR.product_name || selectedLinkForQR.sku_id}
        />
      )}

      {/* Inventory Selection Modal */}
      <InventorySelectionModal
        isOpen={showInventoryModal}
        onClose={() => setShowInventoryModal(false)}
        onSelect={(product) => {
          setSelectedProduct(product);
          setShowInventoryModal(false);
        }}
        selectedSku={selectedProduct}
        title="Select Product for TryOn Link"
        itemsPerPage={10}
      />
    </div>
  );
};

export default Social;
